<template>
  <Modal
    content-class="delete-resource-modal"
    v-bind="modalProps"
    v-model="displayDeleteResourceModal"
    @confirm="confirmDelete"
    @cancel="closeDeleteModal">
    <template v-if="displayReplacementPageCategorySelect">
      <va-select-input
        :data-loading="categoriesLoading"
        class="spacing-bottom--16 spacing-top--16"
        :label="$t('resources.pages.page_category')"
        :placeholder="$t('resources.pages.page_category_placeholder')"
        v-model="replacementPageCategory"
        :choices="replacementPageCategoriesList"
        :item-text="'name'"
        :item-value="'id'">
      </va-select-input>

      <va-disclaimer
        :icon-alignment="'baseline'"
        :type="'accent'"
        :icon-color="'info'"
        :text="$t('resources.page_categories.delete_category_affected_pages_disclaimer')"></va-disclaimer>
    </template>
  </Modal>
</template>

<script>
  import Modal from "@/components/custom/Modal";
  import VaDisclaimer from "@/components/custom/VaDisclaimer.vue";
  import { formErrorMessage } from "@/utility/formErrorMessage";

  export default {
    name: "DeletePageCategoriesModal",
    components: {
      Modal,
      VaDisclaimer,
    },
    props: {
      domainId: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        displayDeleteResourceModal: false,
        itemsToDelete: [],
        deleteResourceContextData: {},
        // Replacement page category select
        displayReplacementPageCategorySelect: false,
        replacementPageCategoriesList: [],
        replacementPageCategory: null,
        categoriesLoading: false,
      };
    },
    computed: {
      // True when deleting multiple list items
      deletingMany() {
        return this.itemsToDelete.length > 1;
      },
      deletingAllItems() {
        return this.itemsToDelete.length === this.deleteResourceContextData?.itemsList?.length;
      },
      // Pages affected if list item(-s) deleted
      affectedPages() {
        return this.itemsToDelete.reduce((sum, current) => {
          return sum + (current.pagesCount ? current.pagesCount : 0);
        }, 0);
      },
      modalProps() {
        const commonModalProps = {
          "cta-color": "error",
          "cta-text": this.$t("generic.buttons.delete"),
          "dialog-icon": "error",
        };
        let customModalProps = {};

        // Page category(-ies) doesn’t have any Articles assigned
        if (!this.affectedPages) {
          customModalProps = {
            title: this.$t("generic.delete_resource", {
              resource: this.getResourceTitle(this.itemsToDelete.length).toLowerCase(),
            }),
            subtitle: this.$t("generic.delete_message"),
          };
        }

        // Page category has Articles assigned and isn’t the only page category on that domain
        if (this.affectedPages && this.deleteResourceContextData?.itemsList?.length > 1 && !this.deletingAllItems) {
          customModalProps = {
            title: this.$t("generic.delete_resource", {
              resource: this.getResourceTitle(this.itemsToDelete.length).toLowerCase(),
            }),
            subtitle: this.$tc(
              `resources.page_categories.${
                this.itemsToDelete.length > 1 ? "delete_category_subtitle_many" : "delete_category_subtitle"
              }`,
              this.affectedPages,
            ),
            "cta-disabled": !this.replacementPageCategory,
          };
        }

        // Page category has Articles assigned and is the only page category on that domain or all items are selected to be deleted
        if (
          (this.affectedPages && this.deleteResourceContextData?.itemsList?.length === 1) ||
          (this.affectedPages && this.deletingAllItems)
        ) {
          customModalProps = {
            title: this.$tc("resources.page_categories.cannot_be_deleted_title", this.itemsToDelete.length),
            subtitle: this.$tc(
              `resources.page_categories.${
                this.deletingMany ? "cannot_be_deleted_subtitle_many" : "cannot_be_deleted_subtitle"
              }`,
              this.affectedPages,
            ),
            "hide-actions": true,
            "dialog-icon": "danger",
            width: "426",
          };
        }

        return {
          ...commonModalProps,
          ...customModalProps,
        };
      },
    },
    beforeMount() {
      // Display / hide modal and set deletable items
      this.$watch("$store.state.page_categories.displayDeleteModal", async displayModal => {
        this.displayDeleteResourceModal = displayModal;

        const { itemsToDelete, deleteResourceContextData } = this.$store.state["page_categories"];

        this.itemsToDelete = itemsToDelete;
        this.deleteResourceContextData = deleteResourceContextData;

        // Page category has Articles assigned and isn’t the only page category on that domain;
        // This is the case when we have to show the change page category select
        if (this.affectedPages && this.deleteResourceContextData?.itemsList?.length > 1 && !this.deletingAllItems) {
          this.displayReplacementPageCategorySelect = true;

          try {
            this.categoriesLoading = true;
            const pageCategories = await this.getPageCategories();
            /**
             * We have to filter out the page category(-ies) that are selected to be deleted.
             * They are not valid as replacement page category(-ies) because they will be deleted.
             */
            this.replacementPageCategoriesList = pageCategories.filter(
              item => !this.itemsToDelete.find(({ id }) => id === item.id),
            );
          } catch (e) {
            console.error(e);
          } finally {
            this.categoriesLoading = false;
          }
        }
      });
    },
    methods: {
      async getPageCategories() {
        const { data } = await this.$admin.dataProvider.getList("page_categories", {
          disablePagination: true,
          filter: {
            domain: this.domainId,
          },
        });

        return data;
      },
      closeDeleteModal() {
        this.$store.dispatch("page_categories/displayDeleteModal", {
          displayDeleteModal: false,
        });

        this.displayReplacementPageCategorySelect = false;
        this.replacementPageCategoriesList = [];
        this.replacementPageCategory = null;
      },
      async confirmDelete() {
        const idsToDelete = this.itemsToDelete.map(({ id }) => id);

        // First we have to change the page category of all affected pages
        if (this.displayReplacementPageCategorySelect) {
          try {
            await Promise.all(
              idsToDelete.map(async id => {
                await this.$admin.dataProvider.customHttpCall(
                  "PUT",
                  `page_categories/${id}/change_category/${this.replacementPageCategory}`,
                );
              }),
            );
          } catch (e) {
            console.error(e);
          }
        }

        if (this.deletingMany) {
          try {
            await this.$store.dispatch("page_categories/deleteMany", {
              ids: idsToDelete,
            });
            this.deleteResourceContextData.postDeleteCallback();
          } catch (e) {
            this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
          } finally {
            this.closeDeleteModal();
          }
        } else {
          try {
            await this.deletePageCategory(idsToDelete[0]);
          } catch (e) {
            this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
          } finally {
            this.closeDeleteModal();
          }
        }
      },

      async deletePageCategory(id) {
        try {
          await this.$admin.dataProvider.customHttpCall("DELETE", `page_categories/${id}`);
          await this.$store.dispatch("api/refresh", "page_categories");
          this.$admin.toast.success(
            this.$t("generic.resource_deleted_successfully", {
              resource: this.getResourceTitle(this.itemsToDelete.length),
            }),
          );
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        }
      },

      getResourceTitle(count = 1) {
        return this.$tc("resources.page_categories.name", count);
      },
    },
  };
</script>

<style lang="scss">
  .delete-resource-modal {
    .va-disclaimer {
      .va-disclaimer__icon {
        margin-top: 3px;
      }
    }
  }
</style>
